import { api } from './api'

/* ------------------------------------------------------------------
   ثبت Service Worker و اشتراک Push

   ==================================================================
   ⚠️ همهٔ توابع اینجا **بی‌خطر شکست می‌خورند**.

   🔴 مرورگرِ بدون پشتیبانی، اجازهٔ ردشده، یا سرورِ بدون کلید VAPID —
   هیچ‌کدام نباید ورود کاربر یا بارگذاری صفحه را بشکنند. اعلان
   قابلیتی جانبی است؛ نبودش آزاردهنده است، ولی خطا دادنش به‌خاطرش
   بدتر.
   ==================================================================
   ------------------------------------------------------------------ */

export type PushState =
  | 'unsupported'
  | 'disabled_on_server'
  | 'denied'
  | 'subscribed'
  | 'unsubscribed'

/** کلید VAPID از base64url به `Uint8Array` — قالبی که مرورگر می‌خواهد. */
function urlBase64ToUint8Array(base64: string): ArrayBuffer {
  const padding = '='.repeat((4 - (base64.length % 4)) % 4)
  const normalised = (base64 + padding).replace(/-/g, '+').replace(/_/g, '/')
  const raw = window.atob(normalised)

  /*
    ⚠️ `ArrayBuffer` برگردانده می‌شود نه `Uint8Array`.

    🔴 امضای `applicationServerKey` در TypeScript جدید `BufferSource`
    است و `Uint8Array<ArrayBufferLike>` با آن یکی نیست. `.buffer`
    همان بایت‌هاست، فقط با نوعی که مرورگر و تایپ هر دو می‌پذیرند.
  */
  const bytes = Uint8Array.from([...raw].map((c) => c.charCodeAt(0)))

  return bytes.buffer as ArrayBuffer
}

export function isPushSupported(): boolean {
  return (
    'serviceWorker' in navigator &&
    'PushManager' in window &&
    'Notification' in window &&
    /*
      🔴 Push فقط در بستر امن کار می‌کند.

      ⚠️ `localhost` استثناست تا توسعه ممکن باشد؛ در شبکهٔ داخلی با
      HTTP ساده، مرورگر بی‌صدا رد می‌کند و اینجا صریح می‌گوییم.
    */
    (window.isSecureContext || location.hostname === 'localhost')
  )
}

/** ثبت Service Worker — بی‌صدا در صورت شکست. */
export async function registerServiceWorker(): Promise<ServiceWorkerRegistration | null> {
  if (!('serviceWorker' in navigator)) return null

  try {
    return await navigator.serviceWorker.register('/sw.js', { scope: '/' })
  } catch {
    return null
  }
}

/** وضعیت فعلی اشتراک. */
export async function pushState(): Promise<PushState> {
  if (!isPushSupported()) return 'unsupported'

  if (Notification.permission === 'denied') return 'denied'

  const registration = await navigator.serviceWorker.getRegistration()
  const subscription = await registration?.pushManager.getSubscription()

  return subscription ? 'subscribed' : 'unsubscribed'
}

/**
 * فعال‌سازی اعلان روی این دستگاه.
 *
 * ⚠️ ترتیب عمدی است: اول کلید سرور، بعد اجازهٔ کاربر.
 *
 * 🔴 اگر برعکس بود، از کاربر اجازه می‌گرفتیم و بعد می‌فهمیدیم سرور
 * اصلاً Push ندارد — و اجازه‌ای که رد شود، دیگر به‌سادگی پس گرفته
 * نمی‌شود.
 */
export async function enablePush(): Promise<PushState> {
  if (!isPushSupported()) return 'unsupported'

  const { data } = await api.get<{ data: { public_key: string | null; enabled: boolean } }>(
    '/me/push/key',
  )

  if (!data.data.enabled || !data.data.public_key) return 'disabled_on_server'

  const permission = await Notification.requestPermission()

  if (permission !== 'granted') return 'denied'

  const registration = (await navigator.serviceWorker.getRegistration())
    ?? (await registerServiceWorker())

  if (!registration) return 'unsupported'

  /* ⚠️ اگر قبلاً اشتراکی هست، همان دوباره به سرور فرستاده می‌شود. */
  const subscription =
    (await registration.pushManager.getSubscription()) ??
    (await registration.pushManager.subscribe({
      /*
        🔴 `userVisibleOnly` اجباری است.

        ⚠️ مرورگر Push خاموش (بدون نمایش اعلان) را نمی‌پذیرد — و درست
        هم می‌کند: پیامِ نامرئی یعنی ردیابی.
      */
      userVisibleOnly: true,
      applicationServerKey: urlBase64ToUint8Array(data.data.public_key),
    }))

  const json = subscription.toJSON() as {
    endpoint?: string
    keys?: { p256dh?: string; auth?: string }
  }

  await api.post('/me/push/subscribe', {
    endpoint: json.endpoint,
    keys: { p256dh: json.keys?.p256dh, auth: json.keys?.auth },
    device_label: navigator.platform || undefined,
  })

  return 'subscribed'
}

/** قطع اعلان روی این دستگاه. */
export async function disablePush(): Promise<PushState> {
  const registration = await navigator.serviceWorker.getRegistration()
  const subscription = await registration?.pushManager.getSubscription()

  if (!subscription) return 'unsubscribed'

  const endpoint = subscription.endpoint

  await subscription.unsubscribe()

  /*
    ⚠️ خطای سرور نباید قطع سمت مرورگر را برگرداند.

    🔴 کاربر گفته «نمی‌خواهم»؛ اگر به‌خاطر خطای شبکه دوباره مشترک
    می‌ماند، خواستهٔ صریحش نادیده گرفته شده بود. ردیفِ یتیم در سرور
    با نخستین خطای ۴۱۰ خودش پاک می‌شود.
  */
  try {
    await api.post('/me/push/unsubscribe', { endpoint })
  } catch {
    /* عمداً خاموش */
  }

  return 'unsubscribed'
}
